import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useClientData } from '../ClientLayout.jsx';
import {
  DEFAULT_PAYMENT_FILTER,
  PAYMENT_FILTER_OPTIONS,
  PAYMENT_LIST_COLUMNS,
  listPaymentRecords,
  loadPaymentListSort,
  paymentDemandHref,
  paymentSortFieldLabel,
  refinePaymentRecords,
  savePaymentListSort,
  sortPaymentRecords,
} from '../../lib/paymentRecords.js';
import { nextColumnSort, sortStatusLabel } from '../../lib/listColumnSort.js';
import ListPagination, { useListPagination } from '../ListPagination.jsx';
import ListSortMark from '../ListSortMark.jsx';

export default function PaymentsPage() {
  const { allMatters } = useClientData();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(() => searchParams.get('q') || '');
  const [filterBy, setFilterBy] = useState(DEFAULT_PAYMENT_FILTER);
  const [sort, setSort] = useState(() => loadPaymentListSort());
  const [filterOpen, setFilterOpen] = useState(false);
  const filterMenuRef = useRef(null);

  useEffect(() => {
    setQ(searchParams.get('q') || '');
  }, [searchParams]);

  const rows = useMemo(() => listPaymentRecords(allMatters), [allMatters]);
  const filtered = useMemo(
    () =>
      sortPaymentRecords(refinePaymentRecords(rows, { query: q, filterId: filterBy }), sort),
    [rows, q, filterBy, sort]
  );
  const pagination = useListPagination(filtered, { storageKey: 'payments' });
  const {
    pageItems,
    page: listPage,
    setPage: setListPage,
  } = pagination;

  const sortLabel = sortStatusLabel(sort, paymentSortFieldLabel);

  function toggleHeaderSort(columnId) {
    const col = PAYMENT_LIST_COLUMNS.find((c) => c.id === columnId);
    setSort(savePaymentListSort(nextColumnSort(sort, columnId, col?.sortType || 'string')));
    if (listPage !== 1) setListPage(1);
  }

  useEffect(() => {
    if (!filterOpen) return undefined;
    function onPointerDown(event) {
      if (filterMenuRef.current?.contains(event.target)) return;
      setFilterOpen(false);
    }
    function onKeyDown(event) {
      if (event.key === 'Escape') setFilterOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [filterOpen]);

  function onFilterChange(nextFilter) {
    setFilterBy(nextFilter);
    setListPage(1);
    setFilterOpen(false);
  }

  function resetFilter() {
    onFilterChange(DEFAULT_PAYMENT_FILTER);
  }

  function openPayment(row) {
    navigate(paymentDemandHref(row), {
      state: { from: '/client/payments' },
    });
  }

  return (
    <div className="client-page">
      <div className="section-head">
        <div>
          <h1>Payments</h1>
          <p>
            Payment ledger across your demands. Open a row to review or record payments on the
            demand record.
          </p>
        </div>
        <div className="section-head-actions">
          <div className="list-toolbar-menu" ref={filterMenuRef}>
            <button
              type="button"
              className="btn-ghost list-columns-btn"
              aria-expanded={filterOpen}
              aria-haspopup="dialog"
              onClick={() => setFilterOpen((open) => !open)}
            >
              Filter
            </button>
            {filterOpen ? (
              <div className="list-columns-menu" role="dialog" aria-label="Filter payments">
                <div className="list-columns-menu-head">
                  <strong>Filter</strong>
                  <button type="button" className="text-link strong" onClick={resetFilter}>
                    Reset
                  </button>
                </div>
                <ul className="list-columns-options">
                  {PAYMENT_FILTER_OPTIONS.map((option) => {
                    const selected = filterBy === option.id;
                    return (
                      <li key={option.id}>
                        <label className="list-columns-option">
                          <input
                            type="radio"
                            name="payments-filter"
                            checked={selected}
                            onChange={() => onFilterChange(option.id)}
                          />
                          <span>{option.label}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {!rows.length ? (
        <div className="empty-state">
          No payments recorded yet. Record a payment from a demand’s Payments tab.
        </div>
      ) : (
        <div className="list-view payments-list-view">
          <div className="list-view-toolbar">
            <p className="list-view-count">
              {filtered.length} payment{filtered.length === 1 ? '' : 's'}
              {filtered.length !== rows.length ? ` of ${rows.length}` : ''}
              {sortLabel ? ` · ${sortLabel}` : ''}
            </p>
          </div>

          {filtered.length ? (
            <>
              <div className="debtor-table-wrap">
                <table className="matters-table payments-table">
                  <thead>
                    <tr>
                      {PAYMENT_LIST_COLUMNS.map((col) => {
                        const active = sort.columnId === col.id;
                        return (
                          <th key={col.id}>
                            <button
                              type="button"
                              className={`list-sort-header${active ? ' is-active' : ''}`}
                              onClick={() => toggleHeaderSort(col.id)}
                              aria-label={`Sort by ${col.label} ${
                                active && sort.direction === 'asc'
                                  ? 'descending'
                                  : 'ascending'
                              }`}
                            >
                              <span className="list-col-label">{col.label}</span>
                              <ListSortMark active={active} direction={sort.direction} />
                            </button>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((row) => (
                      <tr
                        key={row.id}
                        className="matter-row is-clickable"
                        onClick={() => openPayment(row)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openPayment(row);
                          }
                        }}
                        tabIndex={0}
                        role="link"
                        aria-label={`Open payments for ${row.debtorName}`}
                      >
                        <td>{row.paidAtLabel}</td>
                        <td>
                          <strong className="list-column-link">{row.debtorName}</strong>
                        </td>
                        <td>
                          {row.demandId ? (
                            <span className="list-column-link">{row.demandId}</span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>{row.amountLabel}</td>
                        <td>{row.kindLabel}</td>
                        <td>{row.reference || '—'}</td>
                        <td>{row.syncLabel}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <ListPagination
                page={pagination.page}
                pageCount={pagination.pageCount}
                pageSize={pagination.pageSize}
                onPageSizeChange={pagination.setPageSize}
                rangeStart={pagination.rangeStart}
                rangeEnd={pagination.rangeEnd}
                canPrev={pagination.canPrev}
                canNext={pagination.canNext}
                onPrev={pagination.goPrev}
                onNext={pagination.goNext}
              />
            </>
          ) : (
            <div className="empty-state">
              No payments match{q.trim() ? ' this search or' : ' this'} filter.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
